import React, { useContext, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import styles from "./ClinicDashboard.scss";
import moment from "moment";
import filter from "lodash/filter";
import find from "lodash/find";
import flatMap from "lodash/flatMap";
import reject from "lodash/reject";
import * as CouponActions from "actions/CouponActions";
import * as NotificationActions from "actions/NotificationsActions";
import * as WellnessActions from "actions/WellnessActions";
import * as ClinicActions from "actions/ClinicActions";
import {loadClinicProgramDashboard, loadClinicProgramServices} from "api/ProgramApi";
import { windowSizeContext } from "AppRoot";
import ActivityGrid from "components/dashboard/ActivityGrid";
import Button from "components/common/Button";
import ClinicBase from "components/layout/ClinicBase";
import GamesDashboardWidget from "components/dashboard/widgets/GamesDashboardWidget";
import ImageCarousel from "components/common/ImageCarousel";
import NotificationModal from "components/notifications/widgets/NotificationModal";
import NotificationsDashboardWidget from "components/dashboard/widgets/NotificationsDashboardWidget";
import PendingCouponsModal from "components/coupons/widgets/PendingCouponsModal";
import PendingVisitsModal from "components/wellness/widgets/PendingVisitsModal";
import RebateGrid from "components/dashboard/RebateGrid";
import SpinnerTakeover from "components/common/SpinnerTakeover";
import { getNodeId } from "utils/AdminData";
import AvailableProgramsDashboardWidget from "components/programs/AvailableProgramsDashboardWidget";
import DashboardTile from "components/dashboard/widgets/DashboardTile";
import EligibilityTrackerQRCode from "components/clinic/widgets/EligibilityTrackerQRCode";
import Modal from "components/common/Modal";
import {
    getClinicInfo,
    getUnreadNotificationCount,
    getPendingCouponCount,
    getPendingVisitCount,
    getDashboardTiles,
    getCurrentGames,
    clinicHasGames,
    clinicHasWellness,
    clinicHasCoupons
} from "utils/ClinicData";
import { getCouponCount, getWellnessCount } from "utils/getPending";
import { PermissionTypes, userHasPermission, userPrimaryLocationType } from "utils/permissions/rolesPermissions";
import HeroMessage from "components/notifications/elements/HeroMessage";
import { getClinicBasePath } from "utils/routingUtils";
import { CLINIC_TAGS } from "constants/AdminControls";
import * as LocationTypes from "constants/LocationTypes";
import { PENDING } from "constants/Notifications";
import { ENROLLED } from "constants/ProgramStatuses";
import * as RoleTypes from "constants/RoleTypes";
import * as UserPermissions from "constants/UserPermissions";
import { handleErrorResponse } from "utils/request";
import * as TimeFilters from "constants/TimeFilterIds";
import * as ProgramActions from "actions/ProgramActions";

export const ClinicUserContext = React.createContext({ isAdmin: false, isProvider: false });

function ClinicDashboard(props) {
    const [loading, setLoading] = useState(false);
    const [clinicProgramsDashboard, setClinicProgramsDashboard] = useState(null);
    const [showCouponModal, setShowCouponModal] = useState(false);
    const [showWellnessModal, setShowWellnessModal] = useState(false);
    const [showQRCode, setShowQRCode] = useState(false);
    const [showAdminQRCode, setShowAdminQRCode] = useState(false);
    const [selectedNotificationId, setSelectedNotificationId] = useState(null);
    const [eligibilityTrackerSetupComplete, setEligibilityTrackerSetupComplete] = useState(false);
    const LAUNCH_BANNER_KEY = "gl-launch-banner-may28-dismissed";
    const [launchBannerDismissed, setLaunchBannerDismissed] = useState(
        () => !!localStorage.getItem(LAUNCH_BANNER_KEY) || new Date() >= new Date("2026-05-29")
    );
    const TODAY = new moment();
    const context = useContext(windowSizeContext) || {};
    const ELIGIBILITY_TRACKER_SERVICE = 10;

    const visibleNotifications = useMemo(() => {
        return reject(props.notifications, n => n.notificationType === "Reminder" || n.isVisibleInNotifications === false);
    }, [props.notifications]);

    useEffect(() => {
        if (props.clinicId) {
            fetchData();
        }
    }, [props.clinicId, props.hasGames, props.canViewClinicBIGamesDashboard]);
    
    useEffect(() => {
        const alerts = filter(props.notifications, n => n.isAlert && !n.dismissed && n.enrollmentState === PENDING && !props.dismissedAlerts.includes(n.notificationId));
        const forceSelectedNotificationId = alerts.length ? alerts[0].notificationId : null;
        setSelectedNotificationId(forceSelectedNotificationId);
    }, [props.notifications, props.dismissedAlerts]);

    const fetchData = async () => {
        setLoading(true);
        const clinicId = props.clinicId; // Optional (for admin view only)

        if (props.canViewClinicCouponAudit && (props.isAdmin || !props.coupons)) {
            props.getUnprocessedCoupons(clinicId);
        }

        if (props.canViewWellnessAudit &&  (props.isAdmin || !props.visits)) {
            props.getUnprocessedWellnessVisits({
                clinicId: clinicId,
                period: TimeFilters.LAST_90_DAYS,
            });
        }

        if (props.userProfile.canClinic && props.canViewNotifications) {
            props.loadNotifications(clinicId);
        }

        if (props.canViewClinicBIGamesDashboard && props.hasGames) {
            props.getClinicGreenlineGames(clinicId);
        }

        try {
            const res = await loadClinicProgramDashboard(clinicId);
            setClinicProgramsDashboard(res.body);
            const eligibilityTrackerService = find(res.body?.programServiceStatuses, { programServiceId: ELIGIBILITY_TRACKER_SERVICE }) || {};
            const hasEligibilityTracker = eligibilityTrackerService?.programServiceStatus === ENROLLED;
            if (hasEligibilityTracker) {
                const res2 = await loadClinicProgramServices(clinicId, 7);
                const serviceDetails = find(res2.body, { programServiceId: ELIGIBILITY_TRACKER_SERVICE }) || {};
                const extraFields = serviceDetails?.formFieldsJson ? JSON.parse(serviceDetails.formFieldsJson) : {};
                setEligibilityTrackerSetupComplete(extraFields?.IsSetup === true);
            }
        } catch (e) {
            handleErrorResponse("loading clinic program dashboard", e);
        } finally {
            setLoading(false);
        }
    }

    const handlePendingCouponButtonClicked = () => {
        setShowCouponModal(true);
    }

    const handlePendingVisitsButtonClicked = () => {
        setShowWellnessModal(true);
    }

    const handleNotificationClicked = (id) => {
        setSelectedNotificationId(id);
    }

    const handleProgramClicked = (id) => {
    //     TODO: go to program
    }

    const handleNotificationModalClose = () => {
        if (selectedNotificationId) {
            props.storeDismissedAlerts(selectedNotificationId);
            setSelectedNotificationId(null);
        }
    }

    const showSpinner = () => {
        let shouldShow = false;

        if(props.statsLoading) {
            shouldShow = true;
        }

        if(!props.tiles) {
            shouldShow = true;
        }

        if((!props.coupons && props.coupons !== 0) && props.canViewClinicCouponAudit) {
            shouldShow = true;
        }

        if(!props.visits && props.visits !== 0) {
            shouldShow = true;
        }

        return shouldShow;
    }

    const renderProcessingButtons = () => {
        if ((!props.coupons && props.coupons !== 0) || (!props.visits && props.visits !== 0) ) {
            return null;
        }

        const couponsPending = getCouponCount(props.stats);
        const wellnessPending = getWellnessCount(props.stats);
        const useCouponModal = ((couponsPending <= 15) && (couponsPending > 0));
        const useWellnessModal = ((wellnessPending <= 15) && (wellnessPending > 0));
        const basePath = getClinicBasePath(props.location.pathname, props.clinicId, props.nodeId);

        return (
            <div className={styles.processButtons}>
                {(props.canViewClinicCouponAudit && props.hasCoupons) && (
                    <>
                        {useCouponModal ?
                            <Button
                                onClick={handlePendingCouponButtonClicked}
                                action
                                title={!couponsPending ? "No current pending coupons" : ""}
                                icon
                            >
                                <i className={"icon far fa-ticket"} /> Process Coupons
                            </Button>
                            :
                            <Link className={styles.routerLink} to={`${basePath}/coupons`}>
                                <Button action icon >
                                    <i className="icon far fa-ticket" /> Process Coupons
                                </Button>
                            </Link>
                        }
                    </>
                )}

                {props.useWellness && (useWellnessModal ? (
                    <Button
                        onClick={handlePendingVisitsButtonClicked}
                        action
                        title={!wellnessPending ? "No pending wellness visits" : ""}
                        icon
                    >
                        <i className={"icon fas fa-user-md"} /> Approve Wellness
                    </Button>
                ) : (
                    <Link className="router-link" to={`${basePath}/wellness`}>
                        <Button action icon>
                            <i className={"icon fas fa-user-md"} /> Approve Wellness
                        </Button>
                    </Link>
                ))}
                {(eligibilityTrackerSetupComplete) && (
                    <>
                        <Button
                            onClick={() => setShowQRCode(true)}
                            action
                            icon
                            type="eligibilityTracker"
                        >
                            <i className="fa fa-qrcode" /> Eligibility Tracker QR Code
                        </Button>
                        {props.canViewEligibilityTrackerAdminQRCode && (
                            <Button
                                onClick={() => setShowAdminQRCode(true)}
                                action
                                icon
                                type="warn"
                            >
                                <i className="fa fa-qrcode" /> Admin Eligibility Tracker QR Code
                            </Button>
                        )}
                    </>
                )}
            </div>
        );
    }

    const renderDashboardActivityTiles = () =>  {
        if (!props.tiles) {
            return null;
        }

        const tiles = [...props.tiles.items];

        return (
            <div className={styles.section}>
                <ActivityGrid
                    title="Activity"
                    stats={!props.stats ? [] : props.stats.items}
                    tiles={tiles}
                    // handlePeriodChanged={this.onPeriodChanged}
                    hasWellness={props.useWellness}
                />
            </div>
        )
    }

    const renderDashboardRebateTiles = () =>  {
        if (!props.rebateTiles?.length) {
            return null;
        }

        return (
            <div className={styles.section}>
                <RebateGrid
                    title="Rebate Counter"
                    tiles={props.rebateTiles}
                />
            </div>
        )
    }

    const renderCalculators = () => {
        return null;

        if (!props.canViewCalculators) {
            // Doesn't have permissions. Simply return.
            // Should be available only to Greenline groups.
            return;
        }

        return (<>
            { props.canViewCalculators && <Link to={`/admin/clinic/${props.clinicId}/calculator/profitability`}>
                <DashboardTile
                    isButton
                    largeIcon="far fa-chart-bar"
                    title="Profitability Calculator"
                />
            </Link> }
            { props.canViewCalculators && <Link to={`/admin/clinic/${props.clinicId}/calculator/opportunity`}>
                <DashboardTile
                    isButton
                    largeIcon="far fa-chart-line"
                    title="Opportunity Calculator"
                />
            </Link> }
        </>)
    }


    const renderRightSection = () => {
        const basePath = getClinicBasePath(props.location.pathname, props.clinicId, props.nodeId);
        const activeGames = (props.hasGames && props.canViewClinicBIGamesDashboard && props.games) ? filter(props.games, game => {
            return moment(game.endDate).add(7, "day").isSameOrAfter(TODAY);
        }) : null;
        const notificationLimit = 6;

        return (
            <div className={styles.rightSection}>
                <div className={styles.section}>
                    {props.canViewNotifications && (
                        <NotificationsDashboardWidget
                            basePath={basePath}
                            limit={notificationLimit}
                            notifications={visibleNotifications || null}
                            unread={props.notificationTotal}
                            onClick={handleNotificationClicked}
                        />
                    )}
                </div>
                <div className={styles.section}>
                    {props.canViewNotifications && (
                        <AvailableProgramsDashboardWidget basePath={basePath} />
                    )}
                </div>
                {(props.hasGames && props.canViewClinicBIGamesDashboard) &&
                    <div className={styles.section}>
                        <GamesDashboardWidget
                            key={`dashboard_games_${props.clinicId||"x"}_${props.nodeId}`}
                            games={activeGames}
                            basePath={basePath}
                            clinicId={props.clinicId}
                            nodeId={props.nodeId}
                            isAdminView={props.isAdmin}
                            isProviderView={props.isProvider}
                        />
                    </div>
                }
            </div>
        );
    }

    const renderDisclaimer = () => {
        if (!props.tiles) {
            return null;
        }

        const lastUpdate = new Date(props.tiles.lastUpdate).toLocaleDateString();

        return (
            <div
                className="text-italic margin-top-sm"
                style={{ color: "#999", fontSize: "0.8em" }}
            >
                * Items above are estimates only - Last Update {lastUpdate}.
            </div>
        );
    }

    const renderReminders = () => {
        if(!props.reminders?.length){
            return null;
        }

        return(
            <div className={styles.bottomSection}>
                <h3>Reminders</h3>
                <div className={styles.reminders}>
                    <ImageCarousel
                        images={flatMap(props.reminders, "bannerImageUri")}
                    />
                </div>
            </div>
        );
    }

    const selectedNotification = selectedNotificationId ? find(props.notifications, {notificationId: selectedNotificationId}) : null;
    // TODO: Dashboard search will vary based on user permissions
    // const searchConfig = props.useWellness ? SearchTypes.WELLNESS_PLANS : SearchTypes.COUPONS;
    return (
        <ClinicUserContext.Provider value={{
            isAdmin: props.isAdmin,
            isProvider: props.isProvider,
            nodeId: props.nodeId,
            clinicId: props.clinicId,
        }}>
            <ClinicBase
                clinicId={props.clinicId}
                adminControls={CLINIC_TAGS}
                docTitle={!!((props.isAdmin || props.isProvider) && props.clinicInfo?.name) ? `${props.clinicInfo.name} Dashboard` : "Dashboard"}
                collapsible={context.isTabletPortrait}
            >
                <div className={styles.root}>
                    {!launchBannerDismissed && (
                        <div style={{marginBottom: "24px"}}>
                            <HeroMessage
                                id="new-website-launch-may28"
                                type="message"
                                label="A New Look Is Coming Soon"
                                description="This week, you'll notice a new design when logging into Greenline. There is no change to your username, password, or how you access the site. After login, everything will continue to look and function the same as it does today. This is the first step in bringing a more modern Greenline experience to our users and you will continue to see updates to the site over time."
                                dismissible
                                onDismissed={() => {
                                    localStorage.setItem(LAUNCH_BANNER_KEY, "true");
                                    setLaunchBannerDismissed(true);
                                }}
                                fullWidth
                            />
                        </div>
                    )}
                    <div className={styles.top}>
                        <div className={styles.mainSection}>
                            {!context.isPhone && renderProcessingButtons()}
                            {renderDashboardActivityTiles()}
                            {renderDisclaimer()}
                            {renderDashboardRebateTiles()}
                            {renderCalculators()}
                        </div>
                        {renderRightSection()}
                    </div>
                    {renderReminders()}
                    <SpinnerTakeover show={showSpinner()}/>
                </div>
                <PendingCouponsModal
                    show={showCouponModal}
                    onClose={() => setShowCouponModal(false)}
                    clinicId={props.clinicId}
                />
                <PendingVisitsModal
                    show={showWellnessModal}
                    onClose={() => setShowWellnessModal(false)}
                    clinicId={props.clinicId}
                />
                <NotificationModal
                    clinicId={props.clinicId}
                    notification={selectedNotification}
                    show={!!selectedNotification}
                    canAcceptNotifications={props.canAcceptNotifications}
                    canDeclineNotifications={props.canDeclineNotifications}
                    onClose={handleNotificationModalClose}
                />
                {eligibilityTrackerSetupComplete && (
                    <>
                        <Modal
                            show={showQRCode}
                            onClose={() => setShowQRCode(false)}
                            small
                            modalTitle="Eligibility Tracker QR Code"
                        >
                            <EligibilityTrackerQRCode clinicId={props.clinicId} />
                        </Modal>
                        {props.canViewEligibilityTrackerAdminQRCode && (
                            <Modal
                                show={showAdminQRCode}
                                onClose={() => setShowAdminQRCode(false)}
                                small
                                modalTitle="Admin Eligibility Tracker QR Code"
                            >
                                <EligibilityTrackerQRCode
                                    clinicId={props.clinicId}
                                    forAdmin
                                />
                            </Modal>
                        )}
                    </>
                )}
                <SpinnerTakeover show={props.working || loading}/>
            </ClinicBase>
        </ClinicUserContext.Provider>
    )
}

export default connect(
    (state, ownProps) => {
        const adminClinicId = Number(ownProps.clinicId);
        const clinicInfo = getClinicInfo(state, adminClinicId);
        const clinicId = clinicInfo ? clinicInfo.clinicId : undefined;
        const nodeId = getNodeId(state, ownProps);
        const userProfile = state.user.userProfile;
        //Use Location rather than role to determine if the user is an Admin or provider
        const isAdmin = userPrimaryLocationType(userProfile, [LocationTypes.NULL]);
        const isProvider = userPrimaryLocationType(userProfile, [LocationTypes.PROVIDER]);
        const canViewClinicCouponAudit = userHasPermission(PermissionTypes.VIEW, UserPermissions.CLINIC_COUPON_AUDIT, userProfile);
        const canViewEligibilityTrackerAdminQRCode = userHasPermission(PermissionTypes.VIEW, UserPermissions.ELIGIBILITY_TRACKER_ADMIN_QR_CODE, userProfile);
        const canViewWellnessAudit = userHasPermission(PermissionTypes.VIEW, UserPermissions.CLINIC_WELLNESS_AUDIT, userProfile);
        const canViewNotifications = userHasPermission(PermissionTypes.VIEW, UserPermissions.NOTIFICATIONS, userProfile);
        const canAcceptNotifications = userHasPermission(PermissionTypes.ACCEPT, UserPermissions.NOTIFICATIONS, userProfile);
        const canDeclineNotifications = userHasPermission(PermissionTypes.DECLINE, UserPermissions.NOTIFICATIONS, userProfile);
        const canViewClinicBIGamesDashboard = userHasPermission(PermissionTypes.VIEW, UserPermissions.CLINIC_BI_GAMES_DASHBOARD, userProfile);
        const clinicWellness = !!clinicHasWellness(state, clinicId);
        
        const rolesToAllow = RoleTypes.ROLES.filter(x => x.userType === "GL").map(x => x.value);
        const allowedUserPermissions = userProfile.roles.filter(userGroup => {
            return rolesToAllow.some(allowedGroup => allowedGroup === userGroup);
         });
        const canViewCalculators = allowedUserPermissions.length > 0;

        return {
            userProfile,
            isAdmin,
            isProvider,
            clinicId,
            nodeId,
            clinicInfo,
            canViewClinicCouponAudit,
            canViewEligibilityTrackerAdminQRCode,
            canViewWellnessAudit,
            canViewNotifications,
            canViewCalculators,
            canAcceptNotifications,
            canDeclineNotifications,
            canViewClinicBIGamesDashboard,
            coupons: state.entities.unprocessedCoupons,
            visits: getPendingVisitCount(state),
            couponCount: getPendingCouponCount(state),
            useWellness: (clinicWellness && canViewWellnessAudit),
            hasGames: clinicHasGames(state, clinicId),
            hasCoupons: clinicHasCoupons(state, clinicId),
            reminders: Object.values(state.entities.reminders),
            notifications: state.entities.notifications,
            notificationTotal: getUnreadNotificationCount(state),
            rebateTiles: state.entities.rebateTiles,
            tiles: getDashboardTiles(state),
            games: getCurrentGames(state),
            stats: state.entities.stats,
            statsLoading: state.entities.stats.statsLoading,
            working: !!state.applicationState.working,
            dismissedAlerts: state.entities.dismissedAlerts,
        };
    },
    (dispatch) => ({
        getClinicGreenlineGames: (clinicId) => dispatch(ClinicActions.getGreenlineGames(clinicId)),
        getUnprocessedCoupons: (clinicId) => dispatch(CouponActions.getUnprocessedCoupons(clinicId)),
        getUnprocessedWellnessVisits: (options) => dispatch(WellnessActions.getUnprocessedWellnessVisits(options)),
        loadNotifications: (clinicId) => dispatch(NotificationActions.loadNotifications(clinicId)),
        storeDismissedAlerts: (dismissedAlerts) => dispatch(NotificationActions.storeDismissedAlerts(dismissedAlerts)),
    }),
)(ClinicDashboard);
